<template>
  <div class="calendar" ref="calendar" @wheel.passive="handleWheel">
      <div 
        @touchstart.passive="handleTouchStart($event, index)"
        @touchmove.passive="handleTouchMove($event)"
        @touchend.passive="handleTouchEnd()" 
        class="month" 
        :style="{
          'transform': `translateY(${month.yposition}px)`
        }" 
        :month="index" 
        :arrindex="month.index"
        :ref="`${month.month}${month.year}`"
        :id="`${month.month}${month.year}`"
        v-for="(month, index) in visibleMonths" 
        :key="`${month.month}${month.year}`">
          <MonthItem 
            @clicked-day="emitSetData"
            :month="month" 
            :selectedWeek="selectedWeek" 
            :daysOfWeek="daysOfWeek"
            :days="getDays(month)" />
      </div>
  </div>
</template>

<script>
import MonthItem from "@/components/calendarScroller/monthItem.vue";
import useUiScroller from "@/components/calendarScroller/uiscroller.js";
const { calculateMonthHeigth } = useUiScroller();



const DRAGUP = -1;
const DRAGDOWN = 1;

export default {
  name: 'MonthDragger',
  emits:["setData","visible-months"],
  props:["selectionmode","selectedWeek","months","limitYears","yearsInRange","miny","maxy"],
  components:{
    MonthItem
  },
  data() {
    const daysOfWeek = ["S", "T", "Q", "Q", "S", "S", "D"];
    return {
      daysOfWeek,
      isDragging: false,
      draggedIndex: 0,
      dragStartY: 0,
      velocity: 0,
      inertiaInterval: null,
      lastTouchY: 0,
      lastTouchTime: 0,
      progress: 0,
      startTime:0,
      currentYPosition: 0,
    }
  },
  computed:{
    visibleMonths() {
      const sortedMonths = [...this.months];
      sortedMonths.sort((a, b) => {
        return a.index - b.index;
      });
      let closestIndex = -3;
      let closestDiff = Infinity;
      sortedMonths.forEach((month, index) => {
        const diff = Math.abs(month.yposition - this.progress);
        if (diff < closestDiff) {
          closestDiff = diff;
          closestIndex = index;
        }
      });
      return sortedMonths.slice(Math.max(closestIndex - 3, 0), closestIndex + 5);
    }
  },
  methods: {
    emitVisibleMonths() {
      const monthElements = this.visibleMonths.map(month => {
        const element = this.$refs[`${month.month}${month.year}`];
        if(element != null){
          return {
            month: month,
            element: element[0]
          };
        }
      });
      this.$emit('visible-months', monthElements);
    },
    handleWheel(event) {
      const scrollDirection = event.deltaY < 0 ? DRAGUP : DRAGDOWN;
      const scrollDistance = Math.abs(event.deltaY) * 3;
      this.moveElements(scrollDistance, scrollDirection);
      this.$nextTick(()=>{
        this.emitVisibleMonths()
      })
    },
    handleTouchStart(event, index) {
      this.dragStartY = event.touches[0].clientY;
      this.lastTouchY = this.dragStartY;
      this.draggedIndex = index;
      this.startTime = performance.now();
      this.lastTouchTime = this.startTime;
      if (this.inertiaInterval) {
        clearInterval(this.inertiaInterval);
        this.inertiaInterval = null;
        this.velocity = 0; // Reset the velocity to stop the scrolling immediately
      }
    },
    handleTouchMove(event) {
      this.handleTouchBaseOnLastPosition(event);
    },
    handleTouchBaseOnLastPosition(event){
      const currentTouchY = event.touches[0].clientY;
      const dragDirection = currentTouchY < this.lastTouchY ? DRAGUP : DRAGDOWN;
      const dragDistance = Math.abs(currentTouchY - this.lastTouchY);
      this.moveElements(dragDistance, dragDirection);
      this.calculateVelocity(currentTouchY);
      this.$nextTick(()=>{
          this.emitVisibleMonths();
      })    
    },
    calculateVelocity(clientY){
      const currentTime = performance.now();
      const timeDelta = currentTime - this.lastTouchTime;
      this.velocity = (clientY - this.lastTouchY) / timeDelta * 300; // Multiply by 1000 to adjust the scroll speed
      this.lastTouchY = clientY;
      this.lastTouchTime = currentTime;
    },
    handleTouchEnd() {
      this.applyInertiaEffect();
    },
    applyInertiaEffect() {
      const friction = 0.96; // Adjust this value for more or less friction
      this.inertiaInterval = setInterval(() => {
        if (Math.abs(this.velocity) <= 0.1) {
          clearInterval(this.inertiaInterval);
          this.inertiaInterval = null;
          return;
        }
        const dragDirection = this.velocity < 0 ? DRAGUP : DRAGDOWN;
        const dragDistance = Math.abs(this.velocity);
        this.moveElements(dragDistance, dragDirection);
        this.$nextTick(()=>{
          this.emitVisibleMonths();
        })
        if (dragDirection == DRAGUP) {
          this.velocity = Math.min(0, this.velocity * friction);
        } else {
          this.velocity = Math.max(0, this.velocity * friction);
        }
      }, 16); // Update every ~16ms to match 60fps
    },
    moveElements(dragDistance, dragDirection) {
      const updatedPosition = this.currentYPosition + (dragDistance * dragDirection) * 0.15;
      let newPosition = updatedPosition;
      if (this.limitYears) {
        if (this.isDragNextMonthAndCurrentPositionLargerThanLastMonth(dragDirection,updatedPosition)) {
          newPosition = this.maxy * -1;
        } else if (this.isDragPreviousMonthAndCurrentPositionIsSmallerThanFirstPosition(dragDirection,updatedPosition)) {
          newPosition = this.miny * -1;
        } else if(updatedPosition < 0 && dragDirection === DRAGUP && (Math.abs(updatedPosition) + calculateMonthHeigth(4)) > this.maxy){
          newPosition = this.maxy * - 1 + calculateMonthHeigth(4) ;
        }
      }
      requestAnimationFrame(() => {
        if(this.$refs.calendar != null){
          this.$refs.calendar.style.transform = `translateY(${newPosition}px)`;
        }
      });
      this.currentYPosition = newPosition;
      this.progress = this.currentYPosition * -1;
    },
    isDragNextMonthAndCurrentPositionLargerThanLastMonth(dragDirection,updatedPosition){
      return dragDirection === DRAGUP && Math.abs(updatedPosition) > this.maxy 
    },
    isDragPreviousMonthAndCurrentPositionIsSmallerThanFirstPosition(dragDirection,updatedPosition){
      return dragDirection === DRAGDOWN && updatedPosition > Math.abs(this.miny);
    }, 
    emitSetData(data) {
      const { month, day, weekNumber } = data;
      const date = new Date(month.year,month.month-1,day.text);
      const weekStart = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate() - date.getDay() + (date.getDay() === 0 ? -6 : 1)
      );
      const weekEnd = new Date(
        date.getFullYear(),
        date.getMonth(),
        weekStart.getDate() + 6
      );
      const week = {
        start: weekStart,
        end: weekEnd,
        weekNumber,
        year: month.year
      }
      this.$emit("setData", week);
    },
    getDays(month) {
      const days = [];
      const today = new Date();
      const firstDay = new Date(month.year,month.month-1,1).getDay();
      const startingDay = firstDay === 0 ? 7 : firstDay; // convert Sunday (0) to 7
      for (let i = 1; i < startingDay; i++) {
        days.push({ key: i, text: "", classes: "day empty" });
      }
      for (let i = 1; i <= month.days; i++) {
        const date = new Date(month.year,month.month-1,i);
        const isToday = date.getFullYear() === today.getFullYear()
          && date.getMonth() === today.getMonth()
          && date.getDate() === today.getDate();
        days.push({ key: i, text: i, classes: `day${isToday ? ' today' : ''}` });
      }
      return days;
    }
  }
}
</script>

<style scoped>

.calendar {
  overflow-y: visible;
  height: 63vh;
  padding: 16px;
  padding-left: 0;
  padding-right: 0;
  -ms-overflow-style: none;
  scrollbar-width: none; 
  position: relative;
}

.calendar::-webkit-scrollbar {
  display: none;
}
.month {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
}
</style>